import { useContext, useEffect, useState, type ReactNode } from 'react';
import { lotKnown, lotsInStock, type LotProposal } from '@ayumia/component-runtime';
import { AppServicesContext } from '../../views/ViewContext';
import { ScanInput } from './ScanCode';
import { balanceQuery, civilShown, lotQuery, readScan } from './scan';
import type { WidgetProps } from './types';

/** Délai avant d'interroger le serveur pendant la frappe. */
const SEARCH_DELAY_MS = 250;

type Proposals = { status: 'loading' } | { status: 'ready'; rows: LotProposal[] } | { status: 'error' };

/**
 * Widget `lot` (ADR-146 §6.1, addendum (6)) d'un champ texte : la réponse est le numéro de lot, saisi,
 * scanné (un code GS1 donne son lot, AI 10) ou choisi parmi les lots en stock de la tâche (lots actifs
 * à solde disponible positif, recherche serveur, date limite civile affichée). Un numéro inconnu reste
 * saisissable avec un avertissement ; le widget ne crée jamais de lot.
 */
export function LotField({ id, field, value, onChange, disabled, invalid, describedBy, today, scan, workItemId }: WidgetProps): ReactNode {
  // Sans services (aperçu, test) : aucune proposition, le numéro reste saisissable.
  const services = useContext(AppServicesContext);
  const code = typeof value === 'string' ? value : '';
  const [search, setSearch] = useState('');
  const [proposals, setProposals] = useState<Proposals>({ status: 'loading' });
  const applicable = field.type === 'string' || field.type === 'text';

  useEffect(() => {
    if (!applicable || services === null) return;
    let alive = true;
    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            const lots = await services.client.listObjects<Record<string, unknown>>('Lot', lotQuery(search, workItemId));
            const ids = lots.data.map((row) => row['id']).filter((lotId) => lotId !== undefined && lotId !== null).map(String);
            const balances = ids.length === 0 ? { data: [] } : await services.client.listObjects<Record<string, unknown>>('StockBalance', balanceQuery(ids, workItemId));
            if (alive) setProposals({ status: 'ready', rows: lotsInStock(lots.data, balances.data) });
          } catch {
            if (alive) setProposals({ status: 'error' });
          }
        })();
      },
      search === '' ? 0 : SEARCH_DELAY_MS,
    );
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [applicable, search, workItemId, services]);

  if (!applicable) return null;
  const rows = proposals.status === 'ready' ? proposals.rows : [];
  const chosen = rows.find((row) => row.lot_code === code.trim());
  const unknown = code.trim() !== '' && proposals.status === 'ready' && !lotKnown(code, rows);
  const read = (raw: string): void => {
    const text = raw.trim();
    if (text === '') {
      onChange(null);
      return;
    }
    // Un code GS1 scanné donne son lot ; sinon, le texte est le numéro.
    const reading = readScan(text, today);
    onChange(reading.ok && reading.values.lot !== undefined ? reading.values.lot : text);
    setSearch('');
  };
  return (
    <div className="ay-lot">
      <span className="ay-scan__row">
        <ScanInput id={id} value={code} onRead={read} onType={(typed) => onChange(typed.trim() === '' ? null : typed.trim())} disabled={disabled} invalid={invalid} describedBy={describedBy} suffix={scan?.suffix} placeholder="N° de lot" />
        {code !== '' && !disabled && (
          <button type="button" className="ay-button ay-button--secondary ay-scan__clear" onClick={() => onChange(null)}>
            Effacer
          </button>
        )}
      </span>
      {chosen?.expiry_date !== undefined && <span className="ay-lot__expiry">Date limite : {civilShown(chosen.expiry_date)}</span>}
      {unknown && (
        <span className="ay-field__warning" role="status">
          Lot introuvable en stock : vérifiez le numéro.
        </span>
      )}
      {!disabled && services !== null && (
        <div className="ay-lot__stock">
          <input type="search" className="ay-lot__search" aria-label="Chercher un lot en stock" placeholder="Chercher un lot en stock" value={search} onChange={(event) => setSearch(event.target.value)} />
          {proposals.status === 'loading' && <span className="ay-selector__note">Chargement…</span>}
          {proposals.status === 'error' && <span className="ay-selector__note">Lots en stock indisponibles : le numéro reste saisissable.</span>}
          {proposals.status === 'ready' && rows.length === 0 && <span className="ay-selector__note">Aucun lot en stock.</span>}
          {rows.length > 0 && (
            <ul className="ay-lot__list" aria-label="Lots en stock">
              {rows.map((row) => (
                <li key={row.lot_id}>
                  <button type="button" className={`ay-lot__option${row.lot_code === code.trim() ? ' ay-lot__option--chosen' : ''}`} aria-pressed={row.lot_code === code.trim()} onClick={() => onChange(row.lot_code)}>
                    <span className="ay-lot__code">{row.lot_code}</span>
                    {row.expiry_date !== undefined && <span className="ay-lot__date">{civilShown(row.expiry_date)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
